import React, { useState, useEffect, useCallback } from 'react';
import './sent-emails.css';
import getSentEmails from '../../../api/graphql/queries/getSentEmails';
import { buildSentEmailsVariables, defaultDateRange } from '../../../utils/sentEmails';
import LoadIcon from '../../LoadIcon';
import SentEmailsToolbar from './SentEmailsToolbar';
import SentEmailsTable from './SentEmailsTable';
import SentEmailDrawer from './SentEmailDrawer';

// Sent emails tab: a read-only view of the archive of emails ps-back sent
// (ps-email-archive/CONTRACT.md §5), newest first, one "Load more" page at a time.
// There is no total count: the archive is DynamoDB, which has none.

const PAGE_SIZE = 50;

const initialFilters = () => ({
  recipient: '',
  subject: '',
  status: '',
  kind: '',
  ...defaultDateRange(new Date()),
});

const EMPTY_LIST = {
  items: [], nextCursor: null, isAuth: true, error: null, failed: false,
};

// Appending a page must never duplicate a row (React keys are messageIds).
const appendUnique = (existing, incoming) => {
  const seen = new Set(existing.map(item => item.messageId));
  return [...existing, ...incoming.filter(item => item && !seen.has(item.messageId))];
};

const SentEmailsContainer = () => {
  // filters + cursor in ONE object, updated in one setState per user action (the
  // LoyaltyContainer convention): a filter change must reset the cursor in the
  // same render, or the fetch effect would run once with the stale cursor.
  //   cursor null → first page, replaces the list
  //   cursor set  → next page, appends to it
  // Every update creates a new object, so Refresh re-runs the effect even when
  // nothing in it changed.
  const [queryState, setQueryState] = useState(() => ({ filters: initialFilters(), cursor: null }));
  const { filters, cursor } = queryState;

  const [list, setList] = useState(EMPTY_LIST);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const appending = !!cursor;
    setIsLoading(true);

    getSentEmails(buildSentEmailsVariables(filters, cursor, PAGE_SIZE)).then((result) => {
      // A newer query (filter change, refresh, unmount) superseded this one.
      if (cancelled) return;
      setIsLoading(false);
      setList((prev) => {
        const base = appending ? prev : EMPTY_LIST;
        if (!result) return { ...base, failed: true, error: null };
        if (!result.isAuth) return { ...EMPTY_LIST, isAuth: false };
        if (result.error) return { ...base, error: result.error, failed: false };
        const items = result.items || [];
        return {
          items: appending ? appendUnique(prev.items, items) : items,
          nextCursor: result.nextCursor || null,
          isAuth: true,
          error: null,
          failed: false,
        };
      });
    });

    return () => { cancelled = true; };
  }, [queryState]);

  const handleFilterChange = (partial) => {
    setQueryState(prev => ({ filters: { ...prev.filters, ...partial }, cursor: null }));
  };

  const handleRefresh = () => {
    setQueryState(prev => ({ filters: prev.filters, cursor: null }));
  };

  const handleLoadMore = () => {
    if (!list.nextCursor || isLoading) return;
    setQueryState(prev => ({ ...prev, cursor: list.nextCursor }));
  };

  const handleClose = useCallback(() => setSelected(null), []);

  const loadingFirstPage = isLoading && !cursor;
  const loadingMore = isLoading && !!cursor;

  let content;
  if (!list.isAuth) {
    content = <div className="sent-empty">You are not authorized to view sent emails.</div>;
  } else if (list.error === 'not_configured') {
    content = (
      <div className="banner info">
        Sent emails isn&apos;t set up for this environment yet.
      </div>
    );
  } else if (loadingFirstPage) {
    content = (
      <div className="sent-loading">
        <LoadIcon />
        Loading sent emails…
      </div>
    );
  } else {
    const problem = list.failed || list.error;
    content = (
      <>
        {problem ? (
          <div className="banner bad" role="alert">
            {list.error
              ? `Unable to load sent emails (${list.error}).`
              : 'Unable to load sent emails.'}
            <span className="right">
              <button type="button" className="btn ghost tiny" onClick={list.items.length ? handleLoadMore : handleRefresh}>
                Try again
              </button>
            </span>
          </div>
        ) : null}
        {list.items.length === 0 && !problem ? (
          <div className="sent-empty">
            No emails match these filters.
            {filters.recipient ? (
              <span className="hint">The recipient must be the full address; partial matches are not searched.</span>
            ) : null}
          </div>
        ) : null}
        {list.items.length > 0 ? (
          <SentEmailsTable
            items={list.items}
            onOpen={setSelected}
            hasMore={!!list.nextCursor}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
          />
        ) : null}
      </>
    );
  }

  return (
    <div className="sent-emails">
      <div className="headline">
        <h2>Sent emails</h2>
        {list.isAuth && list.items.length > 0 ? (
          <span className="count">
            {`${list.items.length}${list.nextCursor ? '+' : ''} emails`}
          </span>
        ) : null}
      </div>

      {list.isAuth ? (
        <SentEmailsToolbar
          filters={filters}
          onChange={handleFilterChange}
          onRefresh={handleRefresh}
          refreshing={loadingFirstPage}
        />
      ) : null}

      {content}

      {selected ? <SentEmailDrawer row={selected} onClose={handleClose} /> : null}
    </div>
  );
};

export default SentEmailsContainer;
